import { cn } from '@/shared/utils';
import { formatBowlingCenterAddress } from '@/shared/utils/bowlingCenters';

import Finished from '@/components/livescoring/Finished';
import Livegames from '@/components/livescoring/Live';
import TopScores from '@/components/livescoring/TopScores';
import Button from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import useLivescoring from '@/hooks/livescoring/useLivescoring';
import { useTrackPageView } from '@/services/track';

const tabsClassName = cn(
  'rounded-3xl text-primary border-2 border-transparent bg-transparent',
  'data-[state=active]:text-primary data-[state=active]:border-primary'
);

const Livescoring = () => {
  useTrackPageView();
  const { banner, currentBC, activeTab, handleTabChange } = useLivescoring();

  return (
    <>
      {currentBC && (
        <div
          className={cn(
            'py-2 px-3 md:py-3 md:px-4 flex-1 w-screen relative',
            'max-xl:bg-black/50 max-xl:backdrop-blur-sm',
            'xl:sticky xl:-mt-navbar-desktop',
            'xl:top-0 xl:left-1/2 xl:-translate-x-1/2 xl:py-5 xl:z-20',
            'xl:max-w-[50vw] xl:p-0',
            '2xl:max-w-[760px] 2xl:-translate-x-1/2'
          )}
        >
          <div className="flex items-center justify-start xl:justify-center">
            {currentBC.logoUrl && (
              <div className="h-navbar-desktop w-auto flex items-center flex-shrink-0">
                <img
                  src={currentBC.logoUrl}
                  alt={`${currentBC.companyName} logo`}
                  className="object-contain h-[calc(100%-32px)] w-auto"
                />
              </div>
            )}
            {/* 0-width container with overflow-visible for info */}
            <div className="w-0 overflow-visible relative">
              <div className="absolute left-3 md:left-4 xl:left-8 2xl:left-12 top-1/2 -translate-y-1/2 whitespace-nowrap">
                <div className="flex flex-col">
                  <p className="text-sm md:text-base font-semibold leading-6">
                    <span className="inline-flex items-center gap-2 max-w-full">
                      <span className="truncate">{currentBC.companyName}</span>
                      {currentBC.features?.premium && (
                        <img
                          src="/images/star.png"
                          alt="premium"
                          className="size-4 inline-block flex-shrink-0"
                        />
                      )}
                    </span>
                  </p>
                  <div className="flex items-center gap-2">
                    <img
                      loading="lazy"
                      src={
                        currentBC.countryFlag ||
                        `https://flagsapi.com/${currentBC.countryCode.toUpperCase()}/shiny/64.png`
                      }
                      alt="flag of the country"
                      className="w-4 flex-shrink-0"
                    />
                    <p className="text-xs md:text-sm text-secondary truncate">
                      {formatBowlingCenterAddress(currentBC)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {banner.url && (
        <section
          className="h-[calc(100vh/5)] overflow-hidden"
          data-cy="top-banner"
        >
          <a href={banner.link} target="_blank" rel="noopener noreferrer">
            <img
              src={banner.url}
              alt="Lanetalk banner"
              className="w-full h-full object-contain"
            />
          </a>
        </section>
      )}

      <Tabs
        value={activeTab}
        className="py-4 px-4"
        onValueChange={handleTabChange}
      >
        <TabsList className="p-0 bg-transparent md:gap-4 h-11">
          <TabsTrigger value="live" asChild>
            <Button className={tabsClassName}>Live</Button>
          </TabsTrigger>
          <TabsTrigger value="finished" asChild>
            <Button className={tabsClassName}>Finished</Button>
          </TabsTrigger>
          <TabsTrigger value="scores" asChild>
            <Button className={tabsClassName}>Top scores</Button>
          </TabsTrigger>
        </TabsList>
        <TabsContent value="live" className="relative">
          <Livegames />
        </TabsContent>
        <TabsContent value="finished">
          <Finished />
        </TabsContent>
        <TabsContent value="scores">
          <TopScores />
        </TabsContent>
      </Tabs>
    </>
  );
};

export default Livescoring;
